import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import TeamCheckoutDialog from "@/components/TeamCheckoutDialog";
import { Users, PlusCircle } from "lucide-react";

/**
 * Seat-usage pill — shown above the roster grid.
 *
 * Renders "Seats: 18 / 20 used" with a progress bar (green / amber / red) and
 * a "Buy more seats" CTA when the team is approaching cap. Opens the team
 * checkout dialog for additional-seat purchases.
 */
export default function SeatUsagePill() {
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/players"),
      api.get("/billing/me"),
    ]).then(([roster, b]) => {
      const used = (roster.data || []).length;
      const seats = b.data?.type === "team" ? (b.data?.seats_purchased || 20) : 20;
      setData({ used, cap: seats, paid: !!b.data?.paid });
    }).catch((err) => console.warn("[seats] load failed:", err));
  }, []);

  if (!data) return null;

  const pct = data.cap > 0 ? Math.min(100, (data.used / data.cap) * 100) : 0;
  const tone = pct >= 100 ? "red" : pct >= 80 ? "amber" : "green";
  const colors = {
    green: { bg: "#16A34A", text: "#16A34A" },
    amber: { bg: "#F59E0B", text: "#FCD34D" },
    red:   { bg: "#D90429", text: "#FCA5A5" },
  }[tone];

  return (
    <div
      className="rounded-xl ink-card p-4 sm:p-5 flex flex-wrap items-center gap-4 mb-6"
      data-testid="seat-usage-pill"
    >
      <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
        <Users className="h-5 w-5 text-white/80" />
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-baseline gap-2 flex-wrap">
          <div className="text-[10px] tracking-[0.3em] font-bold uppercase text-white/45">Player seats</div>
          <div className="font-mono-num font-display font-black text-white text-lg" data-testid="seat-usage-text">
            {data.used} <span className="text-white/40">/</span> {data.cap}
          </div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: colors.text }}>
            {pct >= 100 ? "Cap reached" : pct >= 80 ? "Nearly full" : `${Math.round(pct)}% used`}
          </div>
        </div>
        <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
          <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: colors.bg }} data-testid="seat-usage-bar" />
        </div>
      </div>
      <Button
        size="sm"
        onClick={() => setDialog(true)}
        className="text-white font-bold rounded-full red-glow shrink-0"
        style={{ backgroundColor: "var(--brand-secondary)" }}
        data-testid="seat-usage-buy"
      >
        <PlusCircle className="h-4 w-4 mr-2" /> Buy more seats
      </Button>
      <TeamCheckoutDialog open={dialog} onOpenChange={setDialog} foundationalRemaining={0} />
    </div>
  );
}
